import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Badge, Form, Modal, ListGroup, Row, Col, Table, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import { Ticket, Comment, Attachment, User, Group, Workflow, ActivityLog, TicketTime, WorkflowCustomField, TicketCustomFieldValue, TicketFinalReport } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { URGENCY_OPTIONS, getUrgencyColor, getUrgencyLabel, type TicketUrgency } from '../utils/urgency';
import { getPriorityColor, PRIORITY_OPTIONS } from '../utils/priority';
import WorkflowVisualizerHorizontal from '../components/WorkflowVisualizerHorizontal';
import AlertModal from '../components/AlertModal';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { downloadFile } from '../utils/fileDownload';
import { formatApiErrorDetail } from '../utils/apiError';
import { ATTACHMENT_ACCEPT, validateAttachmentFile } from '../utils/uploadValidation';
import { applyColorClassToSelection, sanitizeRichTextHtml } from '../utils/richTextSanitizer';
import {
  getWorkflowPriorityVariant,
  getWorkflowStatusLabel,
  getWorkflowStatusVariant,
} from '../utils/workflowVisuals';

const sanitizeHTML = (html: string): string => sanitizeRichTextHtml(html);

// Funções auxiliares
const isWaitingStatus = (status: string): boolean => {
  return status.startsWith('aguardando_');
};

const isFinalStatus = (status: string): boolean => {
  return status === 'fechado' || status === 'cancelado';
};

const stripBidiControlChars = (value: string): string =>
  value.replace(/[\u202A-\u202E\u2066-\u2069]/g, '');

const getStatusColor = (status: string) => {
  return getWorkflowStatusVariant(status);
};

const getStatusLabel = (status: string): string => {
  return getWorkflowStatusLabel(status, 'tickets');
};

const getActivityColor = (activityType: string) => {
  const colors: { [key: string]: string } = {
    criado: 'success',
    distribuido: 'info',
    atribuido: 'primary',
    assumido: 'warning',
    redirecionado: 'warning',
    status_alterado: 'secondary',
    informacao_alterada: 'primary',
    comentario: 'info',
    anexo: 'dark',
    encerrado: 'danger',
  };
  return colors[activityType.toLowerCase()] || 'secondary';
};

/** Alinhado ao backend: resolvido, fechado, cancelado bloqueiam edição de título/descrição */
const statusBlocksTitleDescriptionEdit = (status: string): boolean =>
  status === 'resolvido' || status === 'fechado' || status === 'cancelado';

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const handleNavigateBack = useCallback(() => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(from);
      return;
    }
    navigate(-1);
  }, [navigate, location.state]);
  const { user: currentUser } = useAuth();
  const { showAlert: showModalAlert } = useModal();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [subtasks, setSubtasks] = useState<Ticket[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [canAdvanceStepFromAPI, setCanAdvanceStepFromAPI] = useState(false);
  const [canGoBackStepFromAPI, setCanGoBackStepFromAPI] = useState(false);
  const [canConclude, setCanConclude] = useState(false);
  const [isLastStep, setIsLastStep] = useState(false);
  const [nextStepOptions, setNextStepOptions] = useState<Array<{label?: string; step: string} | string>>([]);
  const [selectedNextStep, setSelectedNextStep] = useState('');
  const [isGoingBackStep, setIsGoingBackStep] = useState(false);
  const [isConcluding, setIsConcluding] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  /** Fluxo exato do ticket (versão antiga pode não aparecer em GET /workflows?only_valid). */
  const [boundWorkflow, setBoundWorkflow] = useState<Workflow | null>(null);
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [ticketTimes, setTicketTimes] = useState<TicketTime | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeCounter, setTimeCounter] = useState(0);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [showWaitingReasonModal, setShowWaitingReasonModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>('');
  const [waitingReason, setWaitingReason] = useState('');
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ title: '', message: '', variant: 'info' as 'success' | 'danger' | 'warning' | 'info', details: '' });
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [isExportingFinalReport, setIsExportingFinalReport] = useState(false);
  const [showFinalReportModal, setShowFinalReportModal] = useState(false);
  const [isLoadingFinalReportOptions, setIsLoadingFinalReportOptions] = useState(false);
  const [finalReportPreview, setFinalReportPreview] = useState<TicketFinalReport | null>(null);
  const [selectedFinalReportFieldIds, setSelectedFinalReportFieldIds] = useState<number[]>([]);
  const [selectedFinalReportFormat, setSelectedFinalReportFormat] = useState<'xlsx' | 'pdf'>('xlsx');
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<TicketCustomFieldValue[]>([]);
  const [isCreatingSubtask, setIsCreatingSubtask] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [showEditTitleDescModal, setShowEditTitleDescModal] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [editDescriptionValue, setEditDescriptionValue] = useState('');
  const [savingTitleDesc, setSavingTitleDesc] = useState(false);

  const [ticketLoadError, setTicketLoadError] = useState<'not_found' | 'forbidden' | null>(null);

  const fetchTicket = useCallback(async () => {
    setTicketLoadError(null);
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}?include_subtasks=true`);
      setTicket(response.data);
    } catch (error) {
      console.error('Erro ao carregar ticket:', error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 403) {
          setTicketLoadError('forbidden');
        } else if (status === 404) {
          setTicketLoadError('not_found');
        }
      }
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
    }
  }, [id]);

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/attachments/tickets/${id}`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
    }
  }, [id]);

  const fetchSubtasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/subtasks`);
      console.log('Subtarefas recebidas:', response.data);
      setSubtasks(response.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar subtarefas:', error);
      console.error('Detalhes do erro:', error.response?.data);
      setSubtasks([]);
    }
  }, [id]);

  const fetchAvailableStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/available-statuses`);
      setAvailableStatuses(response.data.available_statuses || []);
      setCanAdvanceStepFromAPI(response.data.can_advance_step || false);
      setCanGoBackStepFromAPI(response.data.can_go_back_step || false);
      setCanConclude(response.data.can_conclude || false);
      setIsLastStep(response.data.is_last_step || false);
      setNextStepOptions(response.data.next_step_options || []);
    } catch (error) {
      console.error('Erro ao carregar status disponíveis:', error);
      setAvailableStatuses(['aberto', 'em_andamento', 'aguardando_informacao_usuario', 'aguardando_terceiros', 'aguardando_evidencia', 'aguardando_problema', 'aguardando_implantacao', 'resolvido', 'fechado']);
      setCanAdvanceStepFromAPI(false);
      setCanGoBackStepFromAPI(false);
      setCanConclude(false);
      setIsLastStep(false);
      setNextStepOptions([]);
    }
  }, [id]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
    }
  }, [id]);

  const fetchTicketTimes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/times`);
      setTicketTimes(response.data);
    } catch (error) {
      console.error('Erro ao carregar tempos do ticket:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchTicket();
      fetchComments();
      fetchAttachments();
      fetchSubtasks();
      fetchAvailableStatuses();
      fetchActivities();
      fetchTicketTimes();
      fetchGroups(); // Sempre buscar grupos para verificar membro
      if (currentUser?.role === 'admin' || currentUser?.role === 'analista') {
        fetchUsers();
        fetchWorkflows();
      }
      // fetchCustomFields e fetchCustomFieldValues serão chamados quando o ticket for carregado
    }
  }, [id, currentUser, fetchTicket, fetchComments, fetchAttachments, fetchSubtasks, fetchAvailableStatuses, fetchActivities, fetchTicketTimes]);

  // Atualizar contador de tempo a cada segundo apenas se o ticket não estiver fechado
  useEffect(() => {
    if (ticketTimes && ticket && !isFinalStatus(ticket.status)) {
      const interval = setInterval(() => {
        setTimeCounter(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      // Se o ticket estiver fechado, resetar o contador para não adicionar tempo
      setTimeCounter(0);
    }
  }, [ticketTimes, ticket]);


  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      // Precisa de members para canAssign (líder de grupo)
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000, include_members: true },
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const fetchCustomFields = useCallback(async () => {
    if (!id || !ticket?.workflow_id) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/custom-fields/for-step`);
      setCustomFields(response.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar campos customizados:', error);
      setCustomFields([]);
    }
  }, [id, ticket?.workflow_id]);

  const fetchCustomFieldValues = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${id}/custom-fields`);
      setCustomFieldValues(response.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar valores dos campos customizados:', error);
      setCustomFieldValues([]);
    }
  }, [id]);

  // Buscar campos customizados quando o ticket for carregado
  useEffect(() => {
    if (ticket && ticket.workflow_id) {
      fetchCustomFields();
      fetchCustomFieldValues();
    }
  }, [ticket?.id, ticket?.workflow_id, ticket?.current_step, fetchCustomFields, fetchCustomFieldValues]);

  useEffect(() => {
    const wfId = ticket?.workflow_id;
    if (!wfId) {
      setBoundWorkflow(null);
      return;
    }
    let cancelled = false;
    axios
      .get<Workflow>(`${API_URL}/api/workflows/${wfId}`)
      .then((res) => {
        if (!cancelled) setBoundWorkflow(res.data);
      })
      .catch(() => {
        if (!cancelled) setBoundWorkflow(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket?.workflow_id]);

  useEffect(() => {
    const getOptionStepName = (option: {label?: string; step: string} | string): string => {
      if (typeof option === 'string') return option;
      return option.step || option.label || '';
    };

    if (nextStepOptions.length === 1) {
      setSelectedNextStep(getOptionStepName(nextStepOptions[0]));
      return;
    }

    if (nextStepOptions.length > 1) {
      const availableOptions = new Set(nextStepOptions.map(getOptionStepName).filter(Boolean));
      setSelectedNextStep((prev) => (prev && availableOptions.has(prev) ? prev : ''));
      return;
    }

    setSelectedNextStep('');
  }, [nextStepOptions]);

  const showAlert = (title: string, message: string, variant: 'success' | 'danger' | 'warning' | 'info' = 'info', details: string = '') => {
    setAlertModal({ title, message, variant, details });
    setShowAlertModal(true);
  };

  const openFinalReportModal = async () => {
    if (!ticket) return;
    setShowFinalReportModal(true);
    setSelectedFinalReportFormat('xlsx');
    setIsLoadingFinalReportOptions(true);
    try {
      const response = await axios.get<TicketFinalReport>(`${API_URL}/api/tickets/${ticket.id}/final-report`);
      const preview = response.data;
      setFinalReportPreview(preview);
      setSelectedFinalReportFieldIds(preview.custom_fields.map((field) => field.custom_field_id));
    } catch (error) {
      console.error('Erro ao carregar opções do relatório final do ticket', error);
      showModalAlert({
        title: 'Erro',
        message: 'Não foi possível carregar os campos customizáveis do relatório final.',
        variant: 'danger',
      });
      setShowFinalReportModal(false);
    } finally {
      setIsLoadingFinalReportOptions(false);
    }
  };

  const handleExportFinalReport = async () => {
    if (!ticket) return;
    setIsExportingFinalReport(true);
    try {
      const customFieldIdsParam = selectedFinalReportFieldIds.join(',');
      const response = await axios.get(
        `${API_URL}/api/tickets/${ticket.id}/final-report/export.${selectedFinalReportFormat}`,
        {
          responseType: 'blob',
          params: { custom_field_ids: customFieldIdsParam },
        }
      );

      const blob = new Blob([response.data], {
        type: selectedFinalReportFormat === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `relatorio_final_incidente_${ticket.id}.${selectedFinalReportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setShowFinalReportModal(false);
    } catch (error) {
      console.error(`Erro ao exportar relatório final do ticket (${selectedFinalReportFormat})`, error);
      showModalAlert({
        title: 'Erro',
        message: `Não foi possível exportar o relatório final em ${selectedFinalReportFormat.toUpperCase()}.`,
        variant: 'danger',
      });
    } finally {
      setIsExportingFinalReport(false);
    }
  };

  const handleTakeTicket = async () => {
    try {
      await axios.post(`${API_URL}/api/tickets/${id}/take`);
      await fetchTicket();
      await fetchActivities();
      await fetchSubtasks();
      showAlert('Sucesso', 'Ticket assumido com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao assumir ticket', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    // Se o novo status for um dos status de aguardando, mostrar modal para solicitar waiting_reason (opcional)
    if (isWaitingStatus(newStatus)) {
      setPendingStatus(newStatus);
      setShowWaitingReasonModal(true);
      return;
    }
    
    // Para outros status, atualizar diretamente
    try {
      await axios.put(`${API_URL}/api/tickets/${id}`, { status: newStatus });
      fetchTicket();
      fetchAvailableStatuses();
      await fetchTicketTimes();
      showAlert('Sucesso', 'Status atualizado com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar status', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    try {
      await axios.put(`${API_URL}/api/tickets/${id}`, { priority: newPriority });
      fetchTicket();
      showAlert('Sucesso', 'Prioridade atualizada com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar prioridade', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleUrgencyChange = async (newUrgency: TicketUrgency) => {
    try {
      await axios.put(`${API_URL}/api/tickets/${id}`, { urgency: newUrgency });
      fetchTicket();
      showAlert('Sucesso', 'Urgência atualizada com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar urgência', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleSaveTitleDescription = async () => {
    if (!id) return;
    if (!editTitleValue.trim()) {
      showAlert('Atenção', 'O título não pode ficar vazio.', 'warning');
      return;
    }
    setSavingTitleDesc(true);
    try {
      await axios.put(`${API_URL}/api/tickets/${id}`, {
        title: editTitleValue.trim(),
        description: editDescriptionValue,
      });
      setShowEditTitleDescModal(false);
      await fetchTicket();
      await fetchActivities();
      showAlert('Sucesso', 'Título e descrição atualizados com sucesso.', 'success');
    } catch (error: any) {
      showAlert(
        'Erro',
        'Não foi possível atualizar o título ou a descrição.',
        'danger',
        formatApiErrorDetail(error.response?.data?.detail)
      );
    } finally {
      setSavingTitleDesc(false);
    }
  };

  const handleConfirmWaitingStatus = async () => {
    // waiting_reason é opcional agora, pois o status já indica o motivo
    try {
      await axios.put(`${API_URL}/api/tickets/${id}`, { 
        status: pendingStatus,
        waiting_reason: waitingReason?.trim() || ''
      });
      fetchTicket();
      fetchAvailableStatuses();
      await fetchTicketTimes();
      setShowWaitingReasonModal(false);
      setWaitingReason('');
      setPendingStatus('');
      showAlert('Sucesso', `Status atualizado para ${getStatusLabel(pendingStatus)} com sucesso!`, 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar status', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleAdvanceStep = async (selectedStep?: string) => {
    const getOptionStepName = (option: {label?: string; step: string} | string): string => {
      if (typeof option === 'string') return option;
      return option.step || option.label || '';
    };

    let stepName: string | undefined;
    if (selectedStep) {
      stepName = selectedStep;
    } else if (nextStepOptions.length === 1) {
      stepName = getOptionStepName(nextStepOptions[0]);
    }

    if (nextStepOptions.length > 1 && !stepName) {
      showAlert('Atenção', 'Selecione o próximo passo para avançar o fluxo.', 'warning');
      return;
    }

    if (!stepName && nextStepOptions.length === 0) {
      showAlert('Erro', 'Não há próximo passo disponível', 'danger');
      return;
    }
    
    setIsAdvancingStep(true);
    try {
      const finalStepName = typeof stepName === 'string' ? stepName : String(stepName || '');
      const params = finalStepName ? { next_step: finalStepName } : {};
      await axios.post(`${API_URL}/api/tickets/${id}/advance-step`, null, { params });
      await fetchTicket();
      await fetchAvailableStatuses();
      await fetchActivities();
      await fetchTicketTimes();
      showAlert('Sucesso', 'Passo do fluxo avançado com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao avançar passo', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsAdvancingStep(false);
    }
  };

  const handleConclude = async () => {
    setIsConcluding(true);
    try {
      await axios.post(`${API_URL}/api/tickets/${id}/conclude`);
      await fetchTicket();
      await fetchAvailableStatuses();
      await fetchActivities();
      await fetchTicketTimes();
      showAlert('Sucesso', 'Ticket concluído com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao concluir ticket', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsConcluding(false);
    }
  };

  const handleGoBackStep = async () => {
    setIsGoingBackStep(true);
    try {
      await axios.post(`${API_URL}/api/tickets/${id}/go-back-step`);
      await fetchTicket();
      await fetchAvailableStatuses();
      await fetchActivities();
      await fetchTicketTimes();
      showAlert('Sucesso', 'Passo do fluxo retrocedido com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao retroceder passo', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsGoingBackStep(false);
    }
  };

  const handleAssign = async (assignedTo?: number, assignedGroupId?: number) => {
    try {
      await axios.post(`${API_URL}/api/tickets/${id}/assign`, null, {
        params: { assigned_to: assignedTo, assigned_group_id: assignedGroupId }
      });
      await fetchTicket();
      await fetchTicketTimes();
      await fetchActivities();
      setShowAssignModal(false);
      showAlert('Sucesso', 'Ticket atribuído com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atribuir ticket', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleAddComment = async (content: string, isInternalParam: boolean) => {
    if (isAddingComment) {
      return;
    }
    const trimmed = (content || '').trim();
    if (!trimmed) {
      showAlert('Atenção', 'Informe o texto do comentário antes de enviar.', 'warning');
      return;
    }
    setIsAddingComment(true);
    try {
      await axios.post(`${API_URL}/api/tickets/${id}/comments`, {
        content: trimmed,
        is_internal: isInternalParam,
      });
      setCommentText('');
      setIsInternal(false);
      setShowCommentModal(false);
      await fetchComments();
      await fetchActivities();
      showAlert('Sucesso', 'Comentário adicionado com sucesso!', 'success');
    } catch (error: any) {
      console.error('Erro ao adicionar comentário:', error);
      showAlert('Erro', 'Erro ao adicionar comentário', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleCreateSubtask = async (title: string, description: string, priority: string, subtaskTypeId?: number) => {
    // Proteção contra duplo clique - retornar se já estiver processando
    if (isCreatingSubtask) {
      return;
    }
    
    setIsCreatingSubtask(true);
    
    try {
      const requestData: any = {
        title,
        description,
        priority,
        urgency: (ticket?.urgency || 'BAIXA') as TicketUrgency,
        parent_ticket_id: parseInt(id || '0'),
      };

      // Se houver tipo de subtarefa selecionado, buscar o workflow associado
      if (subtaskTypeId && ticket?.workflow_id) {
        try {
          const subtaskTypesResponse = await axios.get(`${API_URL}/api/workflows/${ticket.workflow_id}/subtask-types`);
          const selectedType = subtaskTypesResponse.data.find((t: any) => t.id === subtaskTypeId);
          if (selectedType?.workflow_id_for_subtask) {
            requestData.workflow_id = selectedType.workflow_id_for_subtask;
          }
        } catch (error) {
          console.warn('Erro ao buscar tipo de subtarefa, continuando sem workflow específico:', error);
        }
      }
      
      const response = await axios.post(`${API_URL}/api/tickets/`, requestData);
      
      console.log('Subtarefa criada:', response.data);
      
      // Aguardar um pouco antes de recarregar para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Recarregar subtarefas
      await fetchSubtasks();
      // Também recarregar o ticket para garantir sincronização
      await fetchTicket();
      
      setShowSubtaskModal(false);
      showAlert('Sucesso', 'Subtarefa criada com sucesso!', 'success');
    } catch (error: any) {
      console.error('Erro ao criar subtarefa:', error);
      showAlert('Erro', 'Erro ao criar subtarefa', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsCreatingSubtask(false);
    }
  };

  const handleFileUpload = async () => {
    if (!attachmentFile) {
      showAlert('Aviso', 'Por favor, selecione um arquivo', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', attachmentFile);
    if (attachmentDescription) {
      formData.append('description', attachmentDescription);
    }

    try {
      await axios.post(`${API_URL}/api/attachments/tickets/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchAttachments();
      setShowAttachmentModal(false);
      setAttachmentFile(null);
      setAttachmentDescription('');
      showAlert('Sucesso', 'Anexo adicionado com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao fazer upload do anexo', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };


  const formatTimeMinutesSeconds = (seconds: number): string => {
    const totalSeconds = Math.floor(seconds);
    if (totalSeconds >= 86400) {
      // 24h ou mais: Xd Yh Zmin Ws
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      const parts = [`${days}d`];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}min`);
      parts.push(`${secs}s`);
      return parts.join(' ');
    }
    if (totalSeconds >= 3600) {
      // 1h a menos de 24h: Xh Ymin Zs
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      const parts = [`${hours}h`];
      if (minutes > 0) parts.push(`${minutes}min`);
      parts.push(`${secs}s`);
      return parts.join(' ');
    }
    // Menos de 1h: Xmin Ys
    const totalMinutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${totalMinutes}min ${remainingSeconds}s`;
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (!ticket) {
    if (ticketLoadError === 'forbidden') {
      return (
        <div className="p-3">
          Você não tem permissão para ver este chamado. Se ele foi aberto a partir de uma mudança,
          confirme que você ainda tem acesso à mudança ou peça ao gestor do time.
        </div>
      );
    }
    return <div className="p-3">Ticket não encontrado</div>;
  }

  // Verificar se o usuário pode distribuir tickets (apenas líderes e admins)
  const canAssign = (): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    
    // Verificar se é líder de algum grupo
    if (groups.some(g => g.members?.some(m => m.user_id === currentUser?.id && m.is_leader))) {
      return true;
    }
    
    return false;
  };

  const renderProcessInfoHeader = () => (
    <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <h5 className="mb-0">Informações do Processo</h5>
    </div>
  );

  // Verificar se o usuário pode criar subtarefas (apenas analista responsável pelo ticket)
  const canCreateSubtask = (): boolean => {
    if (!currentUser || !ticket) return false;
    
    // Admin pode sempre criar subtarefas
    if (currentUser.role === 'admin') return true;
    
    // Apenas analistas podem criar subtarefas
    if (currentUser.role !== 'analista') return false;
    
    // Verificar se é responsável pelo ticket (assigned_to ou taken_by)
    const isResponsible = ticket.assigned_to === currentUser.id || ticket.taken_by === currentUser.id;
    
    return isResponsible;
  };

  // Verificar se o usuário pode assumir o ticket
  const canEditTitleDescription = (): boolean => {
    if (!currentUser || !ticket) return false;
    if (statusBlocksTitleDescriptionEdit(ticket.status)) return false;
    if (currentUser.role === 'admin') return true;
    if (ticket.created_by === currentUser.id) return true;
    if (ticket.assigned_to === currentUser.id || ticket.taken_by === currentUser.id) return true;
    return false;
  };

  const canTakeTicket = (): boolean => {
    if (!currentUser || !ticket) return false;
    
    // Não pode assumir se já foi assumido pelo usuário atual
    if (ticket.taken_by === currentUser.id) return false;
    
    // Não pode assumir se já está atribuído diretamente ao usuário atual
    if (ticket.assigned_to === currentUser.id) return false;
    
    // Admin sempre pode assumir se o ticket está atribuído a um grupo
    if (currentUser.role === 'admin' && ticket.assigned_group_id) return true;
    
    // Se está atribuído a um grupo, verificar se o usuário pertence ao grupo
    if (ticket.assigned_group_id) {
      const assignedGroup = groups.find(g => g.id === ticket.assigned_group_id);
      if (assignedGroup && assignedGroup.members) {
        const isMember = assignedGroup.members.some(m => m.user_id === currentUser?.id);
        return isMember;
      }
    }
    
    return false;
  };

  // Verificar se pode avançar o passo
  const canAdvanceStep = (): boolean => {
    if (!currentUser || !ticket) return false;
    
    // Não pode avançar se o ticket já está fechado
    if (isFinalStatus(ticket.status)) return false;
    
    // Verificar permissões primeiro
    const hasPermission = currentUser.role === 'admin' || 
                          ticket.assigned_to === currentUser.id || 
                          ticket.taken_by === currentUser.id;
    
    if (!hasPermission) return false;
    
    // Se há um workflow, confiar no backend que já verificou tudo
    if (ticket.workflow_id) {
      // O backend retorna can_advance_step baseado na lógica do workflow
      return canAdvanceStepFromAPI === true;
    }
    
    // Se não há workflow, permitir avançar sempre (permite mudança manual de status)
    return true;
  };

  // Calcular tempo total e tempo no grupo atual
  // Se o ticket estiver final (fechado/cancelado), usar apenas o valor do backend (sem adicionar timeCounter)
  const isTicketClosed = isFinalStatus(ticket?.status || '');
  const totalTimeSeconds = ticketTimes
    ? ticketTimes.total_time_seconds + (isTicketClosed ? 0 : timeCounter)
    : 0;
  const hasActiveGroupTimer = ticketTimes?.current_group_time !== null && ticketTimes?.current_group_time !== undefined;
  const currentGroupTimeSeconds = hasActiveGroupTimer
    ? (ticketTimes?.current_group_time || 0) + (isTicketClosed ? 0 : timeCounter)
    : 0;
  const currentWorkflow =
    workflows.find((w) => w.id === ticket.workflow_id) ?? boundWorkflow ?? undefined;
  const workflowSteps = (() => {
    if (!currentWorkflow?.steps) return [] as string[];
    try {
      const stepsData = JSON.parse(currentWorkflow.steps);
      if (Array.isArray(stepsData)) {
        return stepsData.map((s: any) => typeof s === 'string' ? s : (s.name || String(s))).filter(Boolean);
      }
      if (stepsData?.steps && Array.isArray(stepsData.steps)) {
        return stepsData.steps.map((s: any) => typeof s === 'string' ? s : (s.name || String(s))).filter(Boolean);
      }
      return [];
    } catch {
      return currentWorkflow.steps.split(',').map((s: string) => s.trim()).filter(Boolean);
    }
  })();

  const displayStepOrderBase = (ticketTimes?.time_in_steps || []).reduce((acc: string[], stepTime) => {
    const stepName = String(stepTime.step_name || '').trim();
    if (!stepName) return acc;
    const stepNameNorm = stepName.toLowerCase();
    if (acc.some((existing) => existing.toLowerCase() === stepNameNorm)) return acc;
    acc.push(stepName);
    return acc;
  }, []);
  const displayStepOrder = [...displayStepOrderBase];
  const firstWorkflowStep = workflowSteps[0];
  if (firstWorkflowStep && !displayStepOrder.some((s) => s.toLowerCase() === firstWorkflowStep.toLowerCase())) {
    displayStepOrder.unshift(firstWorkflowStep);
  }
  if (ticket.current_step && !displayStepOrder.some((s) => s.toLowerCase() === ticket.current_step!.toLowerCase())) {
    displayStepOrder.push(ticket.current_step);
  }

  const canSeeInternalPriority = currentUser?.role === 'admin' || currentUser?.role === 'analista';
  const canEditUrgencyAndPriority = canSeeInternalPriority && (
    currentUser?.role === 'admin' ||
    ticket.assigned_to === currentUser?.id ||
    ticket.taken_by === currentUser?.id
  ) && (ticket.status === 'novo' || ticket.status === 'aberto' || ticket.status === 'em_andamento');

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="outline-secondary" onClick={handleNavigateBack}>
          ← Voltar
        </Button>
        <h2>Ticket #{ticket.id} {ticket.parent_ticket_id && <small className="text-muted">(Subtarefa)</small>}</h2>
        <div className="d-flex gap-2 align-items-center">
          {/* Mostrar seletor de status apenas para responsável ou admin e se ticket não estiver fechado */}
          {(currentUser?.role === 'admin' || 
            ticket.assigned_to === currentUser?.id || 
            ticket.taken_by === currentUser?.id) && 
            !isFinalStatus(ticket.status) && (
            <Form.Select
              value={ticket.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-auto d-inline-block"
            >
              {availableStatuses.map(status => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </Form.Select>
          )}
          {(!(currentUser?.role === 'admin' || 
            ticket.assigned_to === currentUser?.id || 
            ticket.taken_by === currentUser?.id) || 
            isFinalStatus(ticket.status)) && (
            <Badge bg={getStatusColor(ticket.status)}>
              {getStatusLabel(ticket.status)}
            </Badge>
          )}
          {ticket.workflow_id && ticket.expected_status && !isFinalStatus(ticket.status) && (
            <small className="text-muted align-self-center d-none d-lg-inline" title="Status esperado para a etapa atual do fluxo">
              Etapa → {getStatusLabel(ticket.expected_status)}
            </small>
          )}
        </div>
      </div>

      {/* Card do Fluxo de Trabalho - Horizontal no Topo */}
      {ticket.workflow_id && currentWorkflow && (
          <Card className="mb-3">
            <Card.Body>
              <WorkflowVisualizerHorizontal 
                workflow={currentWorkflow} 
                currentStep={ticket.current_step || undefined}
                ticketStatus={ticket.status}
                displayStepOrder={displayStepOrder}
                nextStepCount={nextStepOptions.length}
                onAdvance={() => handleAdvanceStep(selectedNextStep || undefined)}
                onGoBack={handleGoBackStep}
                onConclude={handleConclude}
                canAdvance={canAdvanceStep()}
                canGoBack={canGoBackStepFromAPI}
                canConclude={canConclude}
                isLoading={isAdvancingStep}
                isGoingBack={isGoingBackStep}
                isConcluding={isConcluding}
                isLastStep={isLastStep}
              />
              {canAdvanceStep() && nextStepOptions.length > 1 && (
                <Form.Group className="mt-3">
                  <Form.Label>Próximo passo do fluxo</Form.Label>
                  <Form.Select
                    value={selectedNextStep}
                    onChange={(e) => setSelectedNextStep(e.target.value)}
                    disabled={isAdvancingStep || isGoingBackStep || isConcluding}
                  >
                    <option value="">Selecione um caminho</option>
                    {nextStepOptions.map((option, index) => {
                      const optionLabel = typeof option === 'string' ? option : (option.label || option.step || '');
                      const optionStep = typeof option === 'string' ? option : (option.step || option.label || '');
                      return (
                        <option key={`${optionStep}-${index}`} value={optionStep}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              )}
            </Card.Body>
          </Card>
      )}

      {/* Área de Tempo com Animação */}
      <Card className="mb-3 csp-wave1-ticket-time-card">
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="text-center">
                <h6 className="mb-2">Tempo Total Aberto</h6>
                <h2 className="mb-0 csp-wave1-time-value">
                  {formatTimeMinutesSeconds(totalTimeSeconds)}
                </h2>
                <small className="csp-wave1-time-caption">tempo decorrido</small>
              </div>
            </Col>
            <Col md={6}>
              <div className="text-center">
                <h6 className="mb-2">Tempo no Grupo Atual</h6>
                <h2 className="mb-0 csp-wave1-time-value">
                  {formatTimeMinutesSeconds(currentGroupTimeSeconds)}
                </h2>
                <small className="csp-wave1-time-caption">tempo decorrido</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col md={8}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-start gap-2">
              <h5 className="mb-0 flex-grow-1">{ticket.title}</h5>
              {canEditTitleDescription() && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => {
                    setEditTitleValue(ticket.title);
                    setEditDescriptionValue(ticket.description || '');
                    setShowEditTitleDescModal(true);
                  }}
                >
                  Editar título e descrição
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <p className="csp-wave1-preline-break">{ticket.description}</p>
              <div className="mt-3 d-flex gap-2 align-items-center flex-wrap">
                {canEditUrgencyAndPriority ? (
                  <div className="d-inline-flex align-items-center gap-1">
                    <small className="text-muted fw-semibold">Urgência</small>
                    <Form.Select
                      value={ticket.urgency || 'BAIXA'}
                      onChange={(e) => handleUrgencyChange(e.target.value as TicketUrgency)}
                      className="w-auto"
                      aria-label="Urgência"
                    >
                      {URGENCY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </div>
                ) : (
                  <Badge bg={getUrgencyColor(ticket.urgency)}>
                    Urgência: {getUrgencyLabel(ticket.urgency)}
                  </Badge>
                )}
                {canSeeInternalPriority && (
                  canEditUrgencyAndPriority ? (
                    <div className="d-inline-flex align-items-center gap-1">
                      <small className="text-muted fw-semibold">Prioridade</small>
                      <Form.Select
                        value={ticket.priority ?? 'p4'}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        className="w-auto"
                        aria-label="Prioridade interna"
                      >
                        {PRIORITY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Form.Select>
                    </div>
                  ) : ticket.priority ? (
                    <Badge bg={getPriorityColor(ticket.priority)}>
                      Prioridade: {ticket.priority.toUpperCase()}
                    </Badge>
                  ) : null
                )}
                <Badge bg={getStatusColor(ticket.status)}>
                  {getStatusLabel(ticket.status)}
                </Badge>
                {ticket.current_step && (
                  <Badge bg="info" className="ms-2">
                    Passo: {ticket.current_step}
                  </Badge>
                )}
              </div>
              {isWaitingStatus(ticket.status) && ticket.waiting_reason && (
                <div className="mt-2">
                  <small className="text-muted">
                    <strong>Detalhes adicionais:</strong> {ticket.waiting_reason}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Campos Customizados */}
          {customFields.length > 0 && (() => {
            const canEditFields = ticket && currentUser && (
              currentUser.role === 'admin' ||
              ticket.assigned_to === currentUser.id ||
              ticket.taken_by === currentUser.id
            ) && !isFinalStatus(ticket.status);

            // Agrupar campos por group_name
            const groupedFields: { [key: string]: WorkflowCustomField[] } = {};
            const ungroupedFields: WorkflowCustomField[] = [];

            customFields.forEach(field => {
              if (field.group_name) {
                const groupName = field.group_name;
                if (!groupedFields[groupName]) {
                  groupedFields[groupName] = [];
                }
                groupedFields[groupName].push(field);
              } else {
                ungroupedFields.push(field);
              }
            });

            // Se não há agrupamentos, exibir normalmente
            const hasGroups = Object.keys(groupedFields).length > 0;
            const hasUngrouped = ungroupedFields.length > 0;

            // Se não há grupos e há apenas campos sem agrupamento, exibir sem tabs
            if (!hasGroups && hasUngrouped) {
              return (
                <Card className="mb-3">
                  <Card.Header>{renderProcessInfoHeader()}</Card.Header>
                  <Card.Body>
                    <CustomFieldsEditor
                      ticketId={ticket!.id}
                      workflowId={ticket!.workflow_id!}
                      currentStep={ticket!.current_step}
                      customFields={customFields}
                      customFieldValues={customFieldValues}
                      onSave={() => {
                        fetchCustomFields();
                        fetchCustomFieldValues();
                        fetchTicket();
                      }}
                      canEdit={!!canEditFields}
                      assignedGroupId={ticket!.assigned_group_id}
                    />
                  </Card.Body>
                </Card>
              );
            }

            // Se há apenas um grupo e nenhum campo sem agrupamento, exibir sem tabs
            if (hasGroups && !hasUngrouped && Object.keys(groupedFields).length === 1) {
              const singleGroupFields = Object.values(groupedFields)[0];
              return (
                <Card className="mb-3">
                  <Card.Header>{renderProcessInfoHeader()}</Card.Header>
                  <Card.Body>
                    <CustomFieldsEditor
                      ticketId={ticket!.id}
                      workflowId={ticket!.workflow_id!}
                      currentStep={ticket!.current_step}
                      customFields={singleGroupFields}
                      customFieldValues={customFieldValues}
                      onSave={() => {
                        fetchCustomFields();
                        fetchCustomFieldValues();
                        fetchTicket();
                      }}
                      canEdit={!!canEditFields}
                    />
                  </Card.Body>
                </Card>
              );
            }

            // Exibir com tabs
            return (
              <Card className="mb-3">
                <Card.Header>{renderProcessInfoHeader()}</Card.Header>
                <Card.Body>
                  <Tabs defaultActiveKey={Object.keys(groupedFields)[0] || 'ungrouped'} className="mb-3">
                    {Object.entries(groupedFields).map(([groupName, fields]) => (
                      <Tab key={groupName} eventKey={groupName} title={groupName}>
                        <CustomFieldsEditor
                          ticketId={ticket!.id}
                          workflowId={ticket!.workflow_id!}
                          currentStep={ticket!.current_step}
                          customFields={fields}
                          customFieldValues={customFieldValues}
                          onSave={() => {
                            fetchCustomFields();
                            fetchCustomFieldValues();
                            fetchTicket();
                          }}
                          canEdit={!!canEditFields}
                        />
                      </Tab>
                    ))}
                    {hasUngrouped && (
                      <Tab eventKey="ungrouped" title="Geral">
                        <CustomFieldsEditor
                          ticketId={ticket!.id}
                          workflowId={ticket!.workflow_id!}
                          currentStep={ticket!.current_step}
                          customFields={ungroupedFields}
                          customFieldValues={customFieldValues}
                          onSave={() => {
                            fetchCustomFields();
                            fetchCustomFieldValues();
                            fetchTicket();
                          }}
                          canEdit={!!canEditFields}
                        />
                      </Tab>
                    )}
                  </Tabs>
                </Card.Body>
              </Card>
            );
          })()}


          {!ticket.parent_ticket_id && (
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between">
                <span>Subtarefas {subtasks.length > 0 && `(${subtasks.length})`}</span>
                {!isFinalStatus(ticket.status) && canCreateSubtask() && (
                  <Button size="sm" variant="success" onClick={() => setShowSubtaskModal(true)}>
                    Nova Subtarefa
                  </Button>
                )}
              </Card.Header>
              {subtasks.length > 0 ? (
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Título</th>
                        <th>Status</th>
                        <th>Urgência</th>
                        <th>Responsável</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subtasks.map((subtask) => {
                        const responsibleUser = subtask.taken_by_user || subtask.assignee;
                        return (
                          <tr key={subtask.id}>
                            <td>#{subtask.id}</td>
                            <td>{subtask.title}</td>
                            <td>
                              <Badge bg={getStatusColor(subtask.status)}>
                                {getStatusLabel(subtask.status)}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={getUrgencyColor(subtask.urgency)}>
                                {getUrgencyLabel(subtask.urgency)}
                              </Badge>
                            </td>
                            <td>
                              {responsibleUser ? responsibleUser.name : 'Não atribuído'}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/tickets/${subtask.id}`)}
                              >
                                Ver
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              ) : (
                <Card.Body>
                  <p className="text-muted mb-0">Nenhuma subtarefa criada ainda.</p>
                </Card.Body>
              )}
            </Card>
          )}

          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between">
              <span>Comentários</span>
              {!isFinalStatus(ticket.status) && (
                <Button size="sm" variant="success" onClick={() => setShowCommentModal(true)}>
                  Adicionar Comentário
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {comments.length === 0 ? (
                <p className="text-muted">Nenhum comentário</p>
              ) : (
                <ListGroup variant="flush">
                  {comments.map((comment) => (
                    <ListGroup.Item key={comment.id}>
                      <div className="d-flex justify-content-between">
                        <div>
                          <strong>{comment.user?.name || 'Usuário'}</strong>
                          {comment.is_internal && (
                            <Badge bg="warning" className="ms-2">Interno</Badge>
                          )}
                        </div>
                        <small className="text-muted">
                          {new Date(comment.created_at).toLocaleString('pt-BR')}
                        </small>
                      </div>
                      <div 
                        className="mt-2 mb-0 comment-content csp-wave1-comment-content" 
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(comment.content) }}
                      />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between">
              <span>Log de Atividades</span>
            </Card.Header>
            <Card.Body>
              {activities.length === 0 ? (
                <p className="text-muted">Nenhuma atividade registrada</p>
              ) : (
                <ListGroup variant="flush">
                  {activities.map((activity) => (
                    <ListGroup.Item key={activity.id}>
                      <div className="d-flex justify-content-between mb-1">
                        <div>
                          <Badge bg={getActivityColor(activity.activity_type)} className="me-2">
                            {activity.activity_type}
                          </Badge>
                          <strong>{activity.user?.name || 'Usuário'}</strong>
                        </div>
                        <small className="text-muted">
                          {new Date(activity.created_at).toLocaleString('pt-BR')}
                        </small>
                      </div>
                      <p className="mb-0">{activity.description}</p>
                      {activity.old_value && activity.new_value && (
                        <small className="text-muted">
                          {activity.old_value} → {activity.new_value}
                        </small>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="detail-page-sidebar">
          <Card className="mb-3">
            <Card.Header>Informações</Card.Header>
            <Card.Body>
              <p><strong>Aberto por:</strong> {ticket.creator?.name || 'N/A'}</p>
              <p><strong>Solicitante:</strong> {ticket.requester?.name || ticket.creator?.name || 'N/A'}</p>
              <p><strong>Grupo atribuído:</strong> {
                ticket.assigned_group_id
                  ? groups.find(g => g.id === ticket.assigned_group_id)?.name || `Grupo #${ticket.assigned_group_id}`
                  : 'Não atribuído'
              }</p>
              <p><strong>Analista atribuído:</strong> {ticket.assignee?.name || 'Não atribuído'}</p>
              {ticket.workflow_id && (
                <p><strong>Fluxo:</strong> {currentWorkflow?.name || 'N/A'}</p>
              )}
              {ticket.parent_ticket_id && (
                <p><strong>Ticket Pai:</strong> 
                  <Button 
                    variant="link" 
                    className="p-0 ms-2"
                    onClick={() => navigate(`/tickets/${ticket.parent_ticket_id}`)}
                  >
                    Ver Ticket #{ticket.parent_ticket_id}
                  </Button>
                </p>
              )}
              <p><strong>Data de criação:</strong> {new Date(ticket.created_at).toLocaleString('pt-BR')}</p>
              <p><strong>Última atualização:</strong> {new Date(ticket.updated_at).toLocaleString('pt-BR')}</p>
              {ticket.taken_by_user && (
                <p><strong>Assumido por:</strong> {ticket.taken_by_user.name}</p>
              )}
              <div className="sn-action-buttons mt-2">
                {canTakeTicket() && !isFinalStatus(ticket.status) && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleTakeTicket}
                  >
                    Assumir Ticket
                  </Button>
                )}
                {canAssign() && !isFinalStatus(ticket.status) && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowAssignModal(true)}
                  >
                    Distribuir Ticket
                  </Button>
                )}
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={openFinalReportModal}
                  title="Gerar relatório final do ticket"
                >
                  Gerar Relatório Final
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Anexos</span>
              {!isFinalStatus(ticket.status) && (
                <Button size="sm" variant="success" onClick={() => setShowAttachmentModal(true)}>
                  + Adicionar Anexo
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {attachments.length === 0 ? (
                <p className="text-muted">Nenhum anexo</p>
              ) : (
                <ListGroup variant="flush">
                  {attachments.map((attachment) => (
                    <ListGroup.Item key={attachment.id}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              downloadFile(attachment.file_path, attachment.filename, {
                                onSessionExpired: () => showModalAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                                onError: (msg) => showModalAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                                onFileMissing: () =>
                                  showModalAlert({
                                    title: 'Arquivo indisponível',
                                    message: 'Este anexo não foi encontrado no armazenamento.',
                                    variant: 'warning',
                                  }),
                              });
                            }}
                            className="text-decoration-none csp-wave1-cursor-pointer"
                          >
                            <strong>{attachment.filename}</strong>
                          </a>
                          {attachment.file_missing && (
                            <Badge bg="warning" text="dark" className="ms-2">
                              Indisponível
                            </Badge>
                          )}
                          {attachment.description && (
                            <p className="text-muted mb-1 mt-1">{attachment.description}</p>
                          )}
                          <small className="text-muted">
                            {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(2)} KB` : ''}
                            {' • '}
                            {new Date(attachment.uploaded_at).toLocaleString('pt-BR')}
                          </small>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Card de Tempos por Grupo */}
          {ticketTimes && ticketTimes.time_in_groups && ticketTimes.time_in_groups.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <span>Tempos por Grupo</span>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Tempo</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketTimes.time_in_groups.map((groupTime, index) => {
                      // Se o ticket estiver final (fechado/cancelado), usar apenas o valor do backend (não adicionar timeCounter)
                      const ticketIsFinal = isFinalStatus(ticket?.status || '');
                      const displaySeconds = (groupTime.is_active && ticketTimes.current_group_time && !ticketIsFinal)
                        ? ticketTimes.current_group_time + timeCounter
                        : groupTime.time_spent_seconds;
                      return (
                        <tr key={index}>
                          <td><strong>{groupTime.group_name}</strong></td>
                          <td>
                            <small>
                              {new Date(groupTime.assigned_at).toLocaleString('pt-BR')}
                            </small>
                          </td>
                          <td>
                            <small>
                              {(ticketIsFinal && groupTime.unassigned_at)
                                ? new Date(groupTime.unassigned_at).toLocaleString('pt-BR')
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <strong>{formatTimeMinutesSeconds(displaySeconds)}</strong>
                          </td>
                          <td>
                            {groupTime.is_active ? (
                              <Badge bg="success">Ativo</Badge>
                            ) : (
                              <Badge bg="secondary">{ticketIsFinal ? 'Finalizado' : 'Pausado'}</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Linha de Total */}
                    <tr className="table-info">
                      <td colSpan={3}><strong>TOTAL</strong></td>
                      <td>
                        <strong>{formatTimeMinutesSeconds(ticketTimes.total_time_seconds)}</strong>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Card de Tempos por Etapa do Fluxo */}
          {ticketTimes && ticketTimes.time_in_steps && ticketTimes.time_in_steps.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <span>Tempos por Etapa do Fluxo</span>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Etapa</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Tempo</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketTimes.time_in_steps.map((stepTime, index) => {
                      const ticketIsFinal = isFinalStatus(ticket?.status || '');
                      const ticketStatusToken = String(ticket?.status || '').trim().toLowerCase();
                      const ticketIsPausedByWorkflow =
                        ticketStatusToken === 'aguardando_aprovacao' ||
                        ticketStatusToken === 'aguardando_reenvio';
                      const currentStepKey = String(ticket?.current_step || '').trim().toLowerCase();
                      const stepKey = String(stepTime.step_name || '').trim().toLowerCase();
                      const isCurrentPausedStep =
                        !ticketIsFinal &&
                        ticketIsPausedByWorkflow &&
                        !stepTime.is_active &&
                        currentStepKey.length > 0 &&
                        stepKey === currentStepKey;
                      const displaySeconds = stepTime.time_spent_seconds;
                      return (
                        <tr key={index}>
                          <td><strong>{stepTime.step_name}</strong></td>
                          <td>
                            <small>
                              {new Date(stepTime.started_at).toLocaleString('pt-BR')}
                            </small>
                          </td>
                          <td>
                            <small>
                              {(ticketIsFinal && stepTime.ended_at)
                                ? new Date(stepTime.ended_at).toLocaleString('pt-BR')
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <strong>{formatTimeMinutesSeconds(displaySeconds)}</strong>
                          </td>
                          <td>
                            {stepTime.is_active ? (
                              <Badge bg="success">Ativo</Badge>
                            ) : isCurrentPausedStep ? (
                              <Badge bg="secondary">Pausado</Badge>
                            ) : (
                              <Badge bg="secondary">Finalizado</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Linha de Total */}
                    <tr className="table-info">
                      <td colSpan={3}><strong>TOTAL</strong></td>
                      <td>
                        <strong>{formatTimeMinutesSeconds(
                          ticketTimes.time_in_steps.reduce((sum, step) => sum + step.time_spent_seconds, 0)
                        )}</strong>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Card de Tempos Aguardando */}
          {ticketTimes && ticketTimes.time_waiting && ticketTimes.time_waiting.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <span>Tempos Aguardando</span>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Duração</th>
                      <th>Grupo</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketTimes.time_waiting.map((waitingTime, index) => {
                      const ticketIsFinal = isFinalStatus(ticket?.status || '');
                      const displaySeconds = waitingTime.is_active && !ticketIsFinal
                        ? waitingTime.time_spent_seconds + timeCounter
                        : waitingTime.time_spent_seconds;
                      return (
                        <tr key={index}>
                          <td>
                            {new Date(waitingTime.started_at).toLocaleString('pt-BR')}
                          </td>
                          <td>
                            {(ticketIsFinal && waitingTime.ended_at)
                              ? new Date(waitingTime.ended_at).toLocaleString('pt-BR')
                              : <Badge bg="info">Aguardando</Badge>
                            }
                          </td>
                          <td><strong>{formatTimeMinutesSeconds(displaySeconds)}</strong></td>
                          <td>{waitingTime.group_name || 'N/A'}</td>
                          <td>
                            <small className="text-muted">
                              {waitingTime.waiting_reason || 'Não informado'}
                            </small>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="csp-wave1-summary-total-row">
                      <td colSpan={2}><strong>Total Aguardando</strong></td>
                      <td>
                        <strong>{formatTimeMinutesSeconds(
                          ticketTimes.total_waiting_time_seconds || 
                          ticketTimes.time_waiting.reduce((sum, wt) => sum + (wt.is_active && !isFinalStatus(ticket?.status || '') ? wt.time_spent_seconds + timeCounter : wt.time_spent_seconds), 0)
                        )}</strong>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <CommentModal
        show={showCommentModal}
        onHide={() => {
          if (isAddingComment) return;
          setShowCommentModal(false);
          setCommentText('');
          setIsInternal(false);
        }}
        commentText={commentText}
        setCommentText={setCommentText}
        isInternal={isInternal}
        setIsInternal={setIsInternal}
        onSubmit={handleAddComment}
        canCreateInternal={currentUser?.role === 'admin' || currentUser?.role === 'analista'}
        isSubmitting={isAddingComment}
      />

      {canAssign() && (
        <AssignModal
          show={showAssignModal}
          onHide={() => setShowAssignModal(false)}
          users={users}
          groups={groups}
          workflowId={ticket.workflow_id}
          currentAssignedTo={ticket.assigned_to}
          currentAssignedGroup={ticket.assigned_group_id}
          onAssign={handleAssign}
        />
      )}
      <SubtaskModal
        show={showSubtaskModal}
        onHide={() => setShowSubtaskModal(false)}
        onSubmit={handleCreateSubtask}
        workflowId={ticket?.workflow_id}
        isLoading={isCreatingSubtask}
      />

      <Modal
        show={showEditTitleDescModal}
        onHide={() => {
          if (!savingTitleDesc) setShowEditTitleDescModal(false);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar título e descrição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Título</Form.Label>
            <Form.Control
              type="text"
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              disabled={savingTitleDesc}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={editDescriptionValue}
              onChange={(e) => setEditDescriptionValue(e.target.value)}
              disabled={savingTitleDesc}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditTitleDescModal(false)}
            disabled={savingTitleDesc}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveTitleDescription} disabled={savingTitleDesc || !editTitleValue.trim()}>
            {savingTitleDesc ? 'Salvando…' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Motivo do Aguardando */}
      <Modal show={showWaitingReasonModal} onHide={() => {
        setShowWaitingReasonModal(false);
        setWaitingReason('');
        setPendingStatus('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Informar Detalhes Adicionais</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Detalhes Adicionais (Opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={waitingReason}
              onChange={(e) => setWaitingReason(e.target.value)}
              placeholder="Adicione informações adicionais sobre o motivo do aguardando (opcional)..."
            />
            <Form.Text className="text-muted">
              O status já indica o tipo de aguardando. Você pode adicionar detalhes adicionais aqui se necessário.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowWaitingReasonModal(false);
            setWaitingReason('');
            setPendingStatus('');
          }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmWaitingStatus}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFinalReportModal} onHide={() => setShowFinalReportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Gerar Relatório Final</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingFinalReportOptions ? (
            <p className="text-muted mb-0">Carregando campos customizáveis...</p>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Formato de exportação</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="ticket-final-report-format-xlsx"
                    name="ticket-final-report-format"
                    label="Excel (.xlsx)"
                    checked={selectedFinalReportFormat === 'xlsx'}
                    onChange={() => setSelectedFinalReportFormat('xlsx')}
                  />
                  <Form.Check
                    type="radio"
                    id="ticket-final-report-format-pdf"
                    name="ticket-final-report-format"
                    label="PDF (.pdf)"
                    checked={selectedFinalReportFormat === 'pdf'}
                    onChange={() => setSelectedFinalReportFormat('pdf')}
                  />
                </div>
              </Form.Group>

              <Form.Group>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Campos customizáveis para exportação</Form.Label>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSelectedFinalReportFieldIds(finalReportPreview?.custom_fields.map((field) => field.custom_field_id) || [])}
                    >
                      Marcar todos
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSelectedFinalReportFieldIds([])}
                    >
                      Limpar seleção
                    </Button>
                  </div>
                </div>

                {finalReportPreview?.custom_fields.length ? (
                  <div className="border rounded p-2 csp-wave1-scroll-320">
                    {finalReportPreview.custom_fields.map((field) => (
                      <Form.Check
                        key={field.custom_field_id}
                        type="checkbox"
                        id={`ticket-final-report-field-${field.custom_field_id}`}
                        className="mb-2"
                        label={`${field.label} (${field.field_type})`}
                        checked={selectedFinalReportFieldIds.includes(field.custom_field_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFinalReportFieldIds((prev) =>
                              prev.includes(field.custom_field_id) ? prev : [...prev, field.custom_field_id]
                            );
                          } else {
                            setSelectedFinalReportFieldIds((prev) => prev.filter((id) => id !== field.custom_field_id));
                          }
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">Nenhum campo customizável encontrado para este fluxo.</p>
                )}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFinalReportModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            disabled={isLoadingFinalReportOptions || isExportingFinalReport}
            onClick={handleExportFinalReport}
          >
            {isExportingFinalReport ? 'Exportando...' : `Exportar em ${selectedFinalReportFormat.toUpperCase()}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        details={alertModal.details}
      />

      {/* Modal de Anexo */}
      <Modal show={showAttachmentModal} onHide={() => {
        setShowAttachmentModal(false);
        setAttachmentFile(null);
        setAttachmentDescription('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Adicionar Anexo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Arquivo</Form.Label>
            <input
              type="file"
              className="form-control"
              accept={ATTACHMENT_ACCEPT}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const error = validateAttachmentFile(file);
                if (error) {
                  showAlert('Arquivo inválido', error, 'warning');
                  e.target.value = '';
                  setAttachmentFile(null);
                  return;
                }
                setAttachmentFile(file);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descrição do Anexo</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Descreva o que se trata este anexo..."
              value={attachmentDescription}
              onChange={(e) => setAttachmentDescription(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAttachmentModal(false);
            setAttachmentFile(null);
            setAttachmentDescription('');
          }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleFileUpload}>
            Adicionar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const CommentModal: React.FC<{
  show: boolean;
  onHide: () => void;
  commentText: string;
  setCommentText: (text: string) => void;
  isInternal: boolean;
  setIsInternal: (internal: boolean) => void;
  onSubmit: (content: string, isInternal: boolean) => void | Promise<void>;
  canCreateInternal: boolean;
  isSubmitting?: boolean;
}> = ({ show, onHide, commentText, setCommentText, isInternal, setIsInternal, onSubmit, canCreateInternal, isSubmitting = false }) => {
  const [localSubmitting, setLocalSubmitting] = React.useState(false);
  const submitting = isSubmitting || localSubmitting;
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [selectedColor, setSelectedColor] = React.useState('#000000');
  const [formatState, setFormatState] = React.useState({ bold: false, italic: false, underline: false });

  const syncFormatState = React.useCallback(() => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const anchor = selection.anchorNode;
    if (anchor && !editorRef.current.contains(anchor)) return;
    try {
      setFormatState({
        bold: !!document.queryCommandState('bold'),
        italic: !!document.queryCommandState('italic'),
        underline: !!document.queryCommandState('underline'),
      });
    } catch {
      // ignore unsupported browser commands
    }
  }, []);

  React.useEffect(() => {
    if (show && editorRef.current) {
      // Inicializa o editor apenas ao abrir o modal.
      // Evita reescrever HTML a cada tecla, o que desloca o cursor e inverte digitação.
      editorRef.current.innerHTML = stripBidiControlChars(commentText || '');
      setTimeout(syncFormatState, 0);
    } else if (!show && editorRef.current) {
      // Limpar ao fechar
      editorRef.current.innerHTML = '';
      setFormatState({ bold: false, italic: false, underline: false });
    }
  }, [show, syncFormatState]);

  React.useEffect(() => {
    if (!show) return;
    const handler = () => syncFormatState();
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [show, syncFormatState]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    syncFormatState();
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    if (editorRef.current) {
      applyColorClassToSelection(editorRef.current, color);
      syncFormatState();
      editorRef.current.focus();
    }
  };

  const handleEditorInput = () => {
    syncFormatState();
  };

  const handleEditorBlur = () => {
    if (!editorRef.current) return;
    setCommentText(stripBidiControlChars(editorRef.current.innerHTML));
    syncFormatState();
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const key = e.key.toLowerCase();
    if (key === 'b') {
      e.preventDefault();
      handleFormat('bold');
    } else if (key === 'i') {
      e.preventDefault();
      handleFormat('italic');
    } else if (key === 'u') {
      e.preventDefault();
      handleFormat('underline');
    }
  };

  const handleClearFormat = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || editorRef.current.textContent || '';
      const normalized = stripBidiControlChars(text);
      editorRef.current.innerHTML = normalized;
      setCommentText(editorRef.current.innerHTML);
      setFormatState({ bold: false, italic: false, underline: false });
    }
  };

  const colors = [
    '#000000', '#333333', '#666666', '#999999',
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#FFC0CB', '#A52A2A', '#008000', '#000080'
  ];
  const colorSwatchClassMap: Record<string, string> = {
    '#000000': 'csp-wave1-swatch-black',
    '#333333': 'csp-wave1-swatch-gray-800',
    '#666666': 'csp-wave1-swatch-gray-600',
    '#999999': 'csp-wave1-swatch-gray-500',
    '#FF0000': 'csp-wave1-swatch-red',
    '#00FF00': 'csp-wave1-swatch-green',
    '#0000FF': 'csp-wave1-swatch-blue',
    '#FFFF00': 'csp-wave1-swatch-yellow',
    '#FF00FF': 'csp-wave1-swatch-magenta',
    '#00FFFF': 'csp-wave1-swatch-cyan',
    '#FFA500': 'csp-wave1-swatch-orange',
    '#800080': 'csp-wave1-swatch-purple',
    '#FFC0CB': 'csp-wave1-swatch-pink',
    '#A52A2A': 'csp-wave1-swatch-brown',
    '#008000': 'csp-wave1-swatch-dark-green',
    '#000080': 'csp-wave1-swatch-dark-blue',
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const content = stripBidiControlChars(editorRef.current?.innerHTML || '');
    setCommentText(content);
    if (!content.replace(/<[^>]*>/g, '').trim()) {
      return;
    }
    setLocalSubmitting(true);
    try {
      await onSubmit(content, isInternal);
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop={submitting ? 'static' : true} keyboard={!submitting}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Adicionar Comentário</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Comentário</Form.Label>
          
          {/* Barra de ferramentas */}
          <div className="border rounded-top p-2 bg-light d-flex align-items-center gap-2 flex-wrap csp-wave1-toolbar">
            <Button
              variant={formatState.bold ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormat('bold')}
              title="Negrito (Ctrl+B)"
              aria-pressed={formatState.bold}
            >
              <strong>B</strong>
            </Button>
            <Button
              variant={formatState.italic ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormat('italic')}
              title="Itálico (Ctrl+I)"
              aria-pressed={formatState.italic}
            >
              <em>I</em>
            </Button>
            <Button
              variant={formatState.underline ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormat('underline')}
              title="Sublinhado (Ctrl+U)"
              aria-pressed={formatState.underline}
            >
              <u>U</u>
            </Button>
            
            <div className="vr csp-wave1-separator" />
            
            <div className="d-flex align-items-center gap-1">
              <Form.Label className="mb-0 small">Cor:</Form.Label>
              <Form.Control
                type="color"
                value={selectedColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="csp-wave1-color-picker"
                title="Selecionar cor do texto"
              />
            </div>
            
            <div className="d-flex gap-1">
              {colors.slice(0, 8).map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`btn btn-sm border csp-wave1-color-swatch ${colorSwatchClassMap[color] || ''}`}
                  onClick={() => handleColorChange(color)}
                  title={`Cor ${color}`}
                />
              ))}
            </div>
            
            <div className="vr csp-wave1-separator" />
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClearFormat}
              title="Limpar formatação"
            >
              Limpar
            </Button>
          </div>
          
          {/* Editor de texto rico */}
          <div
            ref={editorRef}
            dir="ltr"
            contentEditable={!submitting}
            onKeyDown={handleEditorKeyDown}
            onInput={handleEditorInput}
            onBlur={handleEditorBlur}
            onPaste={(e) => {
              // Permitir colar apenas texto (remover formatação de outras fontes)
              e.preventDefault();
              const text = stripBidiControlChars(e.clipboardData.getData('text/plain'));
              document.execCommand('insertText', false, text);
              handleEditorInput();
            }}
            className="border rounded-bottom p-2 csp-wave1-editor csp-wave1-editor-plain"
            suppressContentEditableWarning={true}
          />
          
          <Form.Text className="text-muted">
            Use os botões acima para formatar o texto. Suporte a negrito, itálico, sublinhado e cores.
          </Form.Text>
        </Form.Group>
        {canCreateInternal && (
          <Form.Check
            type="checkbox"
            label="Comentário interno"
            checked={isInternal}
            onChange={(e) => setIsInternal(e.target.checked)}
            disabled={submitting}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancelar
        </Button>
        <Button 
          variant="success" 
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Adicionando...' : 'Adicionar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AssignModal: React.FC<{
  show: boolean;
  onHide: () => void;
  users: User[];
  groups: Group[];
  workflowId?: number;
  currentAssignedTo?: number;
  currentAssignedGroup?: number;
  onAssign: (assignedTo?: number, assignedGroupId?: number) => void;
}> = ({ show, onHide, users, groups, workflowId, currentAssignedTo, currentAssignedGroup, onAssign }) => {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  // Carregar grupos e usuários baseado no workflow do ticket
  React.useEffect(() => {
    const loadGroupsAndUsersForWorkflow = async () => {
      if (workflowId) {
        try {
          // Carregar grupos do workflow
          const groupsResponse = await axios.get(`${API_URL}/api/groups`, {
            params: { workflow_id: workflowId }
          });
          setAvailableGroups(groupsResponse.data.filter((g: Group) => g.is_active));
          
          // Carregar usuários dos grupos do workflow
          const usersResponse = await axios.get(`${API_URL}/api/workflows/${workflowId}/users`);
          setAvailableUsers(usersResponse.data || []);
        } catch (error) {
          console.error('Erro ao carregar grupos e usuários do workflow:', error);
          setAvailableGroups([]);
          setAvailableUsers([]);
        }
      } else {
        // Se não há workflow, mostrar todos os grupos e usuários ativos
        setAvailableGroups(groups.filter((g: Group) => g.is_active));
        setAvailableUsers(users.filter((u: User) => u.is_active));
      }
    };

    if (show) {
      loadGroupsAndUsersForWorkflow();
    }
  }, [workflowId, show, groups, users]);

  const handleSubmit = () => {
    const userId = selectedUser ? parseInt(selectedUser) : undefined;
    const groupId = selectedGroup ? parseInt(selectedGroup) : undefined;
    onAssign(userId, groupId);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Distribuir Ticket</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Atribuir a Usuário</Form.Label>
          <Form.Select
            value={selectedUser}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              setSelectedGroup('');
            }}
            disabled={workflowId !== undefined && availableUsers.length === 0}
          >
            <option value="">
              {workflowId !== undefined && availableUsers.length === 0
                ? 'Nenhum usuário disponível para este fluxo'
                : 'Nenhum'}
            </option>
            {availableUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </Form.Select>
          {workflowId !== undefined && (
            <Form.Text className="text-muted">
              Apenas usuários dos grupos associados ao fluxo do ticket estão disponíveis.
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Atribuir a Grupo</Form.Label>
          <Form.Select
            value={selectedGroup}
            onChange={(e) => {
              setSelectedGroup(e.target.value);
              setSelectedUser('');
            }}
            disabled={workflowId !== undefined && availableGroups.length === 0}
          >
            <option value="">
              {workflowId !== undefined && availableGroups.length === 0
                ? 'Nenhum grupo disponível para este fluxo'
                : 'Nenhum'}
            </option>
            {availableGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Form.Select>
          {workflowId !== undefined && (
            <Form.Text className="text-muted">
              Apenas grupos associados ao fluxo do ticket estão disponíveis.
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSubmit}>
          Atribuir
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SubtaskModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onSubmit: (title: string, description: string, priority: string, subtaskTypeId?: number) => void;
  workflowId?: number;
  isLoading?: boolean;
}> = ({ show, onHide, onSubmit, workflowId, isLoading = false }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('p4');
  const [subtaskTypeId, setSubtaskTypeId] = useState<string>('');
  const [subtaskTypes, setSubtaskTypes] = useState<any[]>([]);

  // Carregar tipos de subtarefas quando o modal abrir
  React.useEffect(() => {
    const loadSubtaskTypes = async () => {
      if (show && workflowId) {
        try {
          const response = await axios.get(`${API_URL}/api/workflows/${workflowId}/subtask-types`);
          // Filtrar apenas tipos ativos
          setSubtaskTypes((response.data || []).filter((t: any) => t.is_active));
        } catch (error) {
          console.error('Erro ao carregar tipos de subtarefas:', error);
          setSubtaskTypes([]);
        }
      } else {
        setSubtaskTypes([]);
      }
    };

    loadSubtaskTypes();
  }, [show, workflowId]);

  // Quando tipo de subtarefa muda, atualizar prioridade padrão
  React.useEffect(() => {
    if (subtaskTypeId) {
      const selectedType = subtaskTypes.find(t => t.id === parseInt(subtaskTypeId));
      if (selectedType?.default_priority) {
        setPriority(selectedType.default_priority);
      }
    }
  }, [subtaskTypeId, subtaskTypes]);

  const handleSubmit = () => {
    // Proteção contra duplo clique - retornar se já estiver processando
    if (isLoading) {
      return;
    }
    
    onSubmit(title, description, priority, subtaskTypeId ? parseInt(subtaskTypeId) : undefined);
    setTitle('');
    setDescription('');
    setPriority('p4');
    setSubtaskTypeId('');
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setPriority('p4');
    setSubtaskTypeId('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Nova Subtarefa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {subtaskTypes.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Tipo de Subtarefa</Form.Label>
            <Form.Select 
              value={subtaskTypeId} 
              onChange={(e) => setSubtaskTypeId(e.target.value)}
            >
              <option value="">Selecione um tipo (opcional)</option>
              {subtaskTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} {type.description ? `- ${type.description}` : ''}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Selecione o tipo de subtarefa. Se não selecionar, a subtarefa usará o fluxo do ticket pai.
            </Form.Text>
          </Form.Group>
        )}
        <Form.Group className="mb-3">
          <Form.Label>Título <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Prioridade</Form.Label>
          <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="p1">P1</option>
            <option value="p2">P2</option>
            <option value="p3">P3</option>
            <option value="p4">P4</option>
          </Form.Select>
          {subtaskTypeId && (
            <Form.Text className="text-muted">
              Prioridade padrão do tipo de subtarefa selecionado aplicada automaticamente.
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={!title.trim() || !description.trim() || isLoading}>
          {isLoading ? 'Criando...' : 'Criar Subtarefa'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketDetail;
